import { useEffect, useState } from "react";
import { roundOffIfDecimal } from "features/Common/modules/DataTypes/utils/numbers";
import { initiateJuspay } from "features/Payment/utils/Payment.utils";
import { is_empty } from "features/Common/utils/common.utils";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { getUserChecks } from "utils/AuthUtil";
import { logError } from "utils/error";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { app_route_ids, app_route_keys } from "constants/urlPaths";
import {
  MINIMUM_TOPUP_AMOUNT,
  MINIMUM_WITHDRAWAL_AMOUNT,
  ROUNDOFF_UPTO,
  WALLET_BALANCE_KEY,
  walletKeys,
} from "../constants/Wallets.constants";
import { WALLET_APIS, WALLET_APIS_PATHNAMES } from "../constants/Wallet.apis";
import { useWalletStore } from "../store/WalletStore";
import { navigateTo } from "utils/urlUtils";
import { useRefresh } from "react-admin";

export const useWallets = ({ isFetchWalletBalanceEnabled } = {}) => {
  const isWalletActive = useWalletStore((state) => state.isWalletActive);
  const setIsWalletActive = useWalletStore((state) => state.setIsWalletActive);
  const isTopupModalOpen = useWalletStore((state) => state.isTopupModalOpen);
  const isWithdrawalModalOpen = useWalletStore(
    (state) => state.isWithdrawalModalOpen
  );
  const isSettlementViewDetailsModalOpen = useWalletStore(
    (state) => state.isSettlementViewDetailsModalOpen
  );
  const refresh = useRefresh();
  // receiving the value present in the wallet of the user
  const { data: walletBalance, refetch: refreshWalletBalance } = useCustomQuery(
    {
      url: WALLET_APIS.get_progress,
      params: {
        request_params: `["${walletKeys.walletBalance}","${walletKeys.availableWalletBalance}"]`,
      },
      queryKey: [WALLET_BALANCE_KEY],
      enabled: !!isFetchWalletBalanceEnabled,
    }
  );

  const [orderId, setOrderId] = useState(null);

  const { data: topupFundsStatusCheck } = useCustomQuery({
    queryKey: [WALLET_APIS.recharge_status, orderId],
    url: WALLET_APIS_PATHNAMES.recharge_status(orderId),
    enabled: !!orderId,
  });

  const { notify } = useNotifications();

  const { mutate: withdrawFunds } = useCustomMutation({
    url: WALLET_APIS.withdrawal,
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutate: topupFunds } = useCustomMutation({
    url: WALLET_APIS.recharge,
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutate: retrySettlement } = useCustomMutation({
    url: WALLET_APIS.retry_settlement,
    onSuccess: () => {},
    onError: () => {},
  });

  // set if the creator have there exly wallet activated or not
  const setWalletActiveOrInActive = async () => {
    // the api is being used to fetch user data for setting user's exly wallet state i.e. active or inactive
    try {
      const userData = await getUserChecks({ forceCall: true });
      setIsWalletActive(userData?.is_wallet_enabled ? true : false);
    } catch (err) {
      notify(
        err?.response?.data?.message || SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
      logError({
        err,
        customErrorMessage: "Error while initiate Withdrawal",
        when: "calling getIsWalletActive",
        occuredAt: "src/features/Account/modules/Wallets/utils/useWallets.js",
        severity: "blocker",
      });
    }
  };

  const initiateWithdrawal = ({ amount }) => {
    if (amount && parseInt(amount) !== 0) {
      // Avoids passing amounts with a trailing decimal to the API
      if (String(amount).endsWith(".")) {
        amount = amount.slice(0, -1);
      }
      if (amount < MINIMUM_WITHDRAWAL_AMOUNT) {
        notify(
          `Minimum amount for withdrawal is ${MINIMUM_WITHDRAWAL_AMOUNT}`,
          notification_color_keys.error
        );
        return;
      }
      const handleSuccess = () => {
        isWithdrawalModalOpen(false);
        refresh();
        refreshWalletBalance();
        notify(
          "Withdrawal initiated successfully.",
          notification_color_keys.success
        );
      };

      const handleError = (error) => {
        notify(
          error?.response?.data?.message || SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        logError({
          error,
          customErrorMessage: "Error while initiate Withdrawal",
          when: "calling initiateWithdrawal",
          occuredAt: "src/features/Account/modules/Wallets/utils/useWallets.js",
          severity: "blocker",
        });
      };

      withdrawFunds(
        { amount: amount },
        { onSuccess: handleSuccess, onError: handleError }
      );
    } else {
      notify("Enter amount for withdrawal", notification_color_keys.error);
    }
  };

  const initiateTopup = ({ amount }) => {
    if (amount && parseInt(amount) !== 0) {
      if (amount < MINIMUM_TOPUP_AMOUNT) {
        notify(
          `Minimum amount for topup is ${MINIMUM_TOPUP_AMOUNT}`,
          notification_color_keys.error
        );
        return;
      }
      const handleSuccess = (response) => {
        if (!is_empty(response?.order)) {
          isTopupModalOpen(false);
          notify(
            "Topup initiated successfully.",
            notification_color_keys.success
          );
          initiateJuspay(response.order);
        } else {
          notify(SOMETHING_WENT_WRONG, notification_color_keys.error);
        }
      };

      const handleError = (error) => {
        notify(
          error?.response?.data?.message || SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        logError({
          error,
          customErrorMessage: "Error while initiate topup",
          when: "calling initiateTopup",
          occuredAt: "src/features/Account/modules/Wallets/utils/useWallets.js",
          severity: "blocker",
        });
      };

      topupFunds(
        {
          amount: amount,
          return_url: `${window.location.origin}/#${
            app_route_ids[app_route_keys.WALLET_ALL_TRANSACTION]
          }`,
        },
        { onSuccess: handleSuccess, onError: handleError }
      );
    } else {
      notify("Enter amount for topup", notification_color_keys.error);
    }
  };

  const retrySettlementRequest = ({ settlementUuid }) => {
    if (settlementUuid) {
      const handleSuccess = () => {
        isSettlementViewDetailsModalOpen(false);
        refresh();
        refreshWalletBalance();
        notify(
          "Settlement initiated successfully.",
          notification_color_keys.success
        );
      };

      const handleError = (error) => {
        notify(
          error?.response?.data?.message || SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        logError({
          error,
          customErrorMessage: "Error while retry settlement request",
          when: "calling retrySettlementRequest",
          occuredAt: "src/features/Account/modules/Wallets/utils/useWallets.js",
          severity: "blocker",
        });
      };

      retrySettlement(
        { settlement_uuid: settlementUuid },
        { onSuccess: handleSuccess, onError: handleError }
      );
    } else {
      notify("Invalid settlement id", notification_color_keys.error);
    }
  };

  const walletDeductionCalc = ({ totalAmount, minPayableAmount = 0 } = {}) => {
    const orderTotal = parseFloat(totalAmount);
    const minPayable = parseFloat(minPayableAmount) || 0;
    const walletAmount = walletBalance?.[walletKeys.walletBalance];
    let postDeductionList = {
      usedFromWallet: 0,
      totalAmount: orderTotal,
    };

    if (orderTotal <= minPayable) {
      return postDeductionList;
    }

    if (walletAmount) {
      if (orderTotal > walletAmount) {
        postDeductionList.totalAmount = roundOffIfDecimal(
          Math.max(orderTotal - parseFloat(walletAmount), minPayable),
          ROUNDOFF_UPTO
        );
      } else {
        postDeductionList.totalAmount = Math.max(0, minPayable);
      }
      postDeductionList.usedFromWallet = roundOffIfDecimal(
        totalAmount - postDeductionList.totalAmount,
        ROUNDOFF_UPTO
      );
    }
    return postDeductionList;
  };

  const handleTopUpWalletClick = () => {
    isTopupModalOpen(true);
    navigateTo(app_route_keys.WALLET_ALL_TRANSACTION);
  };

  useEffect(() => {
    (async () => {
      if (isWalletActive === null) {
        await setWalletActiveOrInActive();
      }
    })();
  }, []);

  return {
    walletBalance,
    isWalletActive,
    topupFundsStatusCheck,
    refreshWalletBalance,
    setOrderId,
    walletDeductionCalc,
    initiateWithdrawal,
    initiateTopup,
    retrySettlementRequest,
    handleTopUpWalletClick,
  };
};
